import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useSnackbar } from "react-simple-snackbar"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck, faCopy } from "@fortawesome/free-solid-svg-icons"
import Button from "./Button"
import { errorSnackBar, successSnackBar } from "./snackBarOptions"
import { fetchSubscriptionStatus } from "../app/features/subscriptionStatusSlice"
import { formatInstantDate } from "../utils/dates"
import "./PixQrCode.css"

const POLL_INTERVAL_MS = 3000
const SUCCESS_REDIRECT_MS = 2000
// Monthly-Pix charge QRs stay payable for about a year: past one day the
// countdown gives way to the expiry date (D64).
const COUNTDOWN_MAX_SECONDS = 24 * 60 * 60

const secondsUntil = (iso) => {
  const t = Date.parse(iso ?? "")
  if (Number.isNaN(t)) return 0
  return Math.max(0, Math.floor((t - Date.now()) / 1000))
}

const formatCountdown = (seconds) => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const pad = (n) => String(n).padStart(2, "0")
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

// Last resort when the Clipboard API is unavailable (http, old browsers).
const legacyCopy = (text) => {
  const el = document.createElement("textarea")
  el.value = text
  el.setAttribute("readonly", "")
  el.style.position = "fixed"
  el.style.opacity = "0"
  document.body.appendChild(el)
  el.select()
  const ok = document.execCommand("copy")
  document.body.removeChild(el)
  return ok
}

const hasAccess = (status) => Boolean(status?.has_access)

/**
 * Pix QR: image + "Copiar código Pix" (primary on phones), countdown until
 * expires_at (the date when more than a day away; charge QRs may have none)
 * and polling of /subscription/status every 3 s while visible. Polling stops
 * on unmount, on expiry and after success. isPaid tells success from a status
 * payload: access for a checkout
 * (default), something else for a switch, a renewal or a reauthorization.
 *
 * qr.payload and qr.encoded_image are bearer data: never log or store them.
 */
export default function PixQrCode({
  qr,
  paymentMethod,
  onRegenerate,
  regenerating,
  onPaid,
  isPaid = hasAccess,
  paidMessage = "Seu acesso ao Cosmoclube já está liberado.",
  expiredMessage = "Gere um novo código para concluir sua assinatura.",
}) {
  const dispatch = useDispatch()
  const [openSuccessSnackbar] = useSnackbar(successSnackBar)
  const [openErrorSnackbar] = useSnackbar(errorSnackBar)
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntil(qr?.expires_at))
  const [paid, setPaid] = useState(false)
  const inFlight = useRef(false)
  // A ref, so an inline isPaid does not restart the polling every render.
  const isPaidRef = useRef(isPaid)
  isPaidRef.current = isPaid

  const hasExpiry = Boolean(qr?.expires_at)
  const expired = hasExpiry && secondsLeft <= 0

  useEffect(() => {
    setSecondsLeft(secondsUntil(qr?.expires_at))
    const t = setInterval(() => setSecondsLeft(secondsUntil(qr?.expires_at)), 1000)
    return () => clearInterval(t)
  }, [qr?.expires_at])

  useEffect(() => {
    if (expired || paid) return undefined

    let active = true
    // A hidden tab does not poll (the status is rate-limited per member);
    // coming back checks at once.
    const poll = async () => {
      if (inFlight.current || document.hidden) return
      inFlight.current = true
      try {
        const data = await dispatch(fetchSubscriptionStatus()).unwrap()
        if (active && isPaidRef.current(data)) setPaid(true)
      } catch {
        // keep polling
      } finally {
        inFlight.current = false
      }
    }

    const t = setInterval(poll, POLL_INTERVAL_MS)
    document.addEventListener("visibilitychange", poll)
    return () => {
      active = false
      clearInterval(t)
      document.removeEventListener("visibilitychange", poll)
    }
  }, [dispatch, expired, paid])

  useEffect(() => {
    if (!paid) return undefined
    const t = setTimeout(() => onPaid?.(), SUCCESS_REDIRECT_MS)
    return () => clearTimeout(t)
  }, [paid, onPaid])

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(qr.payload)
      } else if (!legacyCopy(qr.payload)) {
        throw new Error("copy failed")
      }
      openSuccessSnackbar("Código Pix copiado")
    } catch {
      openErrorSnackbar("Não foi possível copiar. Selecione o código e copie manualmente.")
    }
  }

  if (paid) {
    return (
      <div className="pixQr pixQr--paid" role="status">
        <FontAwesomeIcon icon={faCircleCheck} className="pixQr__paidIcon" />
        <h2>Pagamento confirmado!</h2>
        <p>{paidMessage}</p>
      </div>
    )
  }

  if (expired) {
    return (
      <div className="pixQr pixQr--expired">
        <h2>Este código Pix expirou</h2>
        <p>{expiredMessage}</p>
        <Button text="Gerar novo código" onClick={onRegenerate} isLoading={regenerating} />
      </div>
    )
  }

  const automatic = paymentMethod === "pix_automatic"

  return (
    <div className="pixQr">
      <p className="pixQr__instructions pixQr__instructions--desktop">
        Abra o app do seu banco, escolha <strong>Pix › Ler QR Code</strong> e confirme o pagamento
        {automatic ? " e a autorização do Pix Automático" : ""}.
      </p>
      <p className="pixQr__instructions pixQr__instructions--phone">
        Copie o código, abra o app do seu banco e escolha <strong>Pix › Pix Copia e Cola</strong>
        {automatic ? ". Confirme o pagamento e a autorização do Pix Automático" : ""}.
      </p>

      <div className="pixQr__content">
        <img
          className="pixQr__image"
          src={`data:image/png;base64,${qr.encoded_image}`}
          alt="QR Code Pix"
          width={220}
          height={220}
        />
        <button type="button" className="btn pixQr__copy" onClick={handleCopy}>
          <FontAwesomeIcon icon={faCopy} /> Copiar código Pix
        </button>
      </div>

      {hasExpiry && (
        <p className="pixQr__countdown" aria-live="off">
          {secondsLeft > COUNTDOWN_MAX_SECONDS ? (
            <>
              Válido até <strong>{formatInstantDate(qr.expires_at)}</strong>
            </>
          ) : (
            <>
              Código válido por <strong>{formatCountdown(secondsLeft)}</strong>
            </>
          )}
        </p>
      )}
      <p className="pixQr__waiting" role="status">Aguardando a confirmação do pagamento…</p>
      {paymentMethod === "pix" && (
        <p className="pixQr__note">Você receberá um novo código Pix a cada renovação.</p>
      )}
    </div>
  )
}
